/**
 * Host-aware SPA detection — now backed by the Janus boundary registry.
 *
 * The same Vite bundle is served from multiple CloudFront distributions
 * (Cuidari consumer + Console admin + local dev). Janus
 * (https://janus.francisquini.ai/v1/resolve?host=<origin>) is the canonical
 * source of truth for "what surface does this origin belong to?" — see
 * `docs/specs/janus/00-index.md` Phase 2.B.
 *
 * Two-layer API:
 *
 *   1. `getSurface()` — async, calls Janus on first invocation and
 *      caches on `window.__janusSurface` for the SPA lifetime.
 *      Returns `{ surface, brand }` plus origin metadata.
 *
 *   2. `getHostKind()` / `isAdminHost()` / `isProductHost()` / `getBrand()`
 *      — synchronous primitives still used by `router.ts` at module-init
 *      time (before any async fetch can complete). They read the cached
 *      Janus result if present; otherwise fall back to the hardcoded
 *      hostname tables (`PRODUCT_HOSTS` / `ADMIN_HOSTS`) below. This
 *      guarantees the SPA boots even if Janus is unreachable.
 *
 * Cache shape: `window.__janusSurface` (typed via the `declare global`
 * augmentation at the bottom). Set by `getSurface()` on first success;
 * read by the sync primitives. `main.tsx` warms the cache on app start
 * so component-level reads after the first paint see Janus data.
 *
 * Surface vs HostKind: the spec uses {`consumer`, `admin`, `developer`};
 * the legacy router uses {`product`, `admin`, `unknown`}. They are
 * isomorphic: consumer→product, admin→admin, developer→product
 * (dev origins kept on product tree so the local SPA works the same as
 * app.cuidari.com). `unknown` only appears when Janus 404s AND no fallback
 * matches — defensively treated as product to avoid lockout.
 *
 * TODO(host-aware bundle): the runtime filter does NOT shrink the bundle —
 * Vite still ships every module. A follow-up should add a build-time
 * `VITE_HOST_KIND` env var that prunes the unused tree at compile time
 * (separate CloudFront origins, two build artifacts).
 */
export type HostKind = 'product' | 'admin' | 'unknown'

/** Canonical surface names as declared by the Janus manifest. */
type Surface = 'consumer' | 'admin' | 'developer'

/** Resolved surface result — what `getSurface()` returns. */
interface SurfaceResolution {
  surface: Surface
  brand: string
  /** Where the resolution came from (telemetry / debugging). */
  source: 'janus' | 'fallback'
  /** The origin that was resolved. */
  origin: string
}

const PRODUCT_HOSTS = new Set<string>([
  'app.cuidari.com',
  'ariadne.francisquini.ai', // legacy: keeps serving product UX during cutover
  'localhost', // dev → product
  '127.0.0.1',
])

const ADMIN_HOSTS = new Set<string>([
  'console.francisquini.ai',
  'console.localhost',
  'console.localtest.me',
])

/**
 * Brand returned by the fallback table when the origin serving this bundle
 * is NOT one we can name — i.e. Janus is unreachable (or has not answered
 * yet) AND the hostname is in neither `PRODUCT_HOSTS` nor `ADMIN_HOSTS`.
 *
 * WHY THIS IS NOT `'cuidari'`
 * ---------------------------
 * The brand is no longer cosmetic: it is the product axis of the data lake.
 * `analytics/source.ts` derives `source_system` from it, and the echo relay
 * partitions rows by `source_system`. Naming an unrecognised origin
 * `cuidari` is therefore not a harmless default — it writes another
 * product's (or an unknown origin's) rows INTO the Cuidari lake, where
 * they become indistinguishable from real Cuidari traffic and quietly
 * poison every count, funnel and LGPD export built on top of it.
 *
 * The Origin is the real trust boundary (the relay already 403s a
 * producer whose Origin is not in its `allowed_origins`), so the only
 * honest answer for an origin we cannot name is "I do not know".
 * Downstream, an unknown brand means DO NOT EMIT — a missing row is
 * recoverable; a mislabelled row silently is not.
 *
 * Routing is deliberately NOT affected: an unrecognised host still renders
 * the consumer tree (`fallbackSurface` keeps `surface: 'consumer'`) so a
 * Janus blip can never lock a legitimate visitor out of the app.
 */
export const UNKNOWN_BRAND = 'unknown'

const JANUS_RESOLVE_URL = 'https://janus.francisquini.ai/v1/resolve'

function defaultHostname(): string {
  if (typeof window === 'undefined') return ''
  return window.location.hostname
}

function defaultOrigin(): string {
  if (typeof window === 'undefined') return ''
  return window.location.origin
}

/**
 * Map Janus' canonical surface name to the legacy router HostKind.
 * Developer origins ride the product tree (same UX as app.cuidari.com).
 */
export function surfaceToHostKind(surface: Surface): HostKind {
  if (surface === 'admin') return 'admin'
  // consumer + developer both render the product tree
  return 'product'
}

/**
 * Hardcoded-table fallback. Used when Janus is unreachable AND there is
 * no cached resolution. Mirrors the pre-Janus behaviour exactly so the
 * SPA never locks out a legitimate visitor because of a network blip.
 */
function fallbackHostKind(hostname: string = defaultHostname()): HostKind {
  if (PRODUCT_HOSTS.has(hostname)) return 'product'
  if (ADMIN_HOSTS.has(hostname)) return 'admin'
  return 'unknown'
}

/**
 * Hardcoded-table fallback for the BRAND, keyed by the same hostname sets
 * as `fallbackHostKind`. Only a host we actually declare gets a product
 * name; everything else is `UNKNOWN_BRAND` (see that constant for why).
 */
function fallbackBrand(hostname: string = defaultHostname()): string {
  const kind = fallbackHostKind(hostname)
  if (kind === 'admin') return 'francisquini'
  if (kind === 'product') return 'cuidari'
  return UNKNOWN_BRAND
}

/**
 * Build a `SurfaceResolution` from the hardcoded table (used when Janus
 * is unreachable). Mirrors the spec failsafe D4.
 */
function fallbackSurface(origin: string = defaultOrigin()): SurfaceResolution {
  const hostname = (() => {
    try {
      return new URL(origin).hostname
    } catch {
      return defaultHostname()
    }
  })()
  const kind = fallbackHostKind(hostname)
  if (kind === 'admin') {
    return { surface: 'admin', brand: 'francisquini', source: 'fallback', origin }
  }
  // product OR unknown — both fall through to the consumer SURFACE so the
  // SPA never locks out a legitimate visitor on a temporary Janus blip.
  //
  // The BRAND, however, is not laundered: only a host we actually declare
  // in `PRODUCT_HOSTS` gets named `cuidari`. An origin we cannot name gets
  // `UNKNOWN_BRAND` — see the constant's doc comment for why guessing
  // `cuidari` here would write foreign rows into the Cuidari lake.
  return {
    surface: 'consumer',
    brand: fallbackBrand(hostname),
    source: 'fallback',
    origin,
  }
}

/**
 * Read the cached Janus result if `getSurface()` has already resolved
 * successfully in this SPA session. Returns `null` if not yet warmed.
 */
function readCachedSurface(): SurfaceResolution | null {
  if (typeof window === 'undefined') return null
  const cached = (window as typeof window & {
    __janusSurface?: SurfaceResolution
  }).__janusSurface
  return cached ?? null
}

function writeCachedSurface(resolution: SurfaceResolution): void {
  if (typeof window === 'undefined') return
  ;(window as typeof window & {
    __janusSurface?: SurfaceResolution
  }).__janusSurface = resolution
}

/**
 * Resolve the current SPA's surface against the live Janus boundary
 * registry. Caches the result on `window.__janusSurface` for the SPA
 * lifetime so React StrictMode double-renders + cross-route reads do
 * not stampede the endpoint.
 *
 * Falls back to the hardcoded `PRODUCT_HOSTS` / `ADMIN_HOSTS` tables
 * when Janus is unreachable (network error, 5xx, or non-JSON body) so
 * an outage of janus.francisquini.ai does not take the SPA down.
 *
 * Janus 404 (unknown host — e.g. a PR preview that was not declared in
 * `arkheus/manifest/boundaries.yaml`) also falls back to the table.
 */
export async function getSurface(
  origin: string = defaultOrigin(),
): Promise<SurfaceResolution> {
  const cached = readCachedSurface()
  if (cached && cached.origin === origin) return cached

  // Empty origin (SSR / non-browser test env) — return fallback without
  // attempting a network call.
  if (!origin) {
    const resolution = fallbackSurface(origin)
    writeCachedSurface(resolution)
    return resolution
  }

  if (import.meta.env.DEV) {
    const resolution = fallbackSurface(origin)
    writeCachedSurface(resolution)
    return resolution
  }

  try {
    const url = `${JANUS_RESOLVE_URL}?host=${encodeURIComponent(origin)}`
    const res = await fetch(url, {
      method: 'GET',
      // No credentials: the resolve endpoint is fully public per spec D2.
      credentials: 'omit',
      headers: { Accept: 'application/json' },
    })

    if (!res.ok) {
      // 404 = host not declared; any other non-2xx = Janus outage.
      // Both fall back to the table.
      const resolution = fallbackSurface(origin)
      writeCachedSurface(resolution)
      return resolution
    }

    const body = (await res.json()) as {
      surface?: string
      brand?: string
    }

    if (
      body.surface !== 'consumer' &&
      body.surface !== 'admin' &&
      body.surface !== 'developer'
    ) {
      // Janus responded with an unrecognised surface — treat as outage.
      const resolution = fallbackSurface(origin)
      writeCachedSurface(resolution)
      return resolution
    }

    const resolution: SurfaceResolution = {
      surface: body.surface,
      // Janus answered but omitted the brand: fall back to the SAME
      // hardcoded table the sync primitives use, never to a guessed
      // `cuidari`. Otherwise a second product's declared origin would be
      // laundered into the Cuidari lake by a missing field — the exact
      // hole `UNKNOWN_BRAND` exists to close.
      brand: typeof body.brand === 'string' && body.brand.length > 0
        ? body.brand
        : fallbackSurface(origin).brand,
      source: 'janus',
      origin,
    }
    writeCachedSurface(resolution)
    return resolution
  } catch {
    // Network error, DNS failure, fetch rejected — table fallback.
    const resolution = fallbackSurface(origin)
    writeCachedSurface(resolution)
    return resolution
  }
}

/**
 * Synchronous host-kind classifier used by `router.ts` at module init,
 * before any async Janus call can complete.
 *
 * Resolution order:
 *   1. If `getSurface()` already wrote `window.__janusSurface` (e.g.
 *      this is a re-mount after the initial Janus fetch resolved),
 *      project that to a HostKind.
 *   2. Otherwise, classify against the hardcoded host table.
 *
 * The hardcoded table matches the current Janus manifest, so the
 * router tree is identical whether Janus answered before or after
 * module load. Once Janus-driven classification has baked for a release,
 * the hardcoded table can be deleted and Janus becomes load-bearing.
 */
export function getHostKind(host: string = defaultHostname()): HostKind {
  const cached = readCachedSurface()
  if (cached) return surfaceToHostKind(cached.surface)
  return fallbackHostKind(host)
}

/**
 * Synchronous brand resolver — the product axis, mirroring `getHostKind()`
 * exactly (cached Janus resolution first, hardcoded table second).
 *
 * Callers must treat `UNKNOWN_BRAND` as "do not attribute this to any
 * product" rather than as a synonym for the default brand. Today the only
 * consumer is `analytics/source.ts`, which turns an unknown brand into a
 * dropped envelope instead of a `cuidari_app` row.
 *
 * Note on the ibirami path: this bundle is NOT served from an ibirami
 * hostname today, so `PRODUCT_HOSTS` deliberately does not list one —
 * asserting otherwise would be a lie about this SPA's own origins. A
 * second product reaches `brand: 'ibirami'` through Janus resolving its
 * Origin, which is exactly the boundary the relay's per-Origin
 * anti-spoofing gate already enforces.
 */
export function getBrand(host: string = defaultHostname()): string {
  const cached = readCachedSurface()
  if (cached) return cached.brand
  return fallbackBrand(host)
}

export function isAdminHost(host?: string): boolean {
  return getHostKind(host ?? defaultHostname()) === 'admin'
}

export function isProductHost(host?: string): boolean {
  return getHostKind(host ?? defaultHostname()) === 'product'
}

/**
 * Absolute URL that the admin host links back to when an operator
 * needs to escape to the consumer product app (e.g. "Voltar pro Cuidari").
 */
export const PRODUCT_HOME_URL = 'https://app.cuidari.com/'

/**
 * Test helper — clears the cached Janus resolution. Not exported via
 * the public barrel; reach for it in tests that need a clean slate
 * between cases.
 */
export function __clearSurfaceCache(): void {
  if (typeof window === 'undefined') return
  delete (window as typeof window & { __janusSurface?: SurfaceResolution })
    .__janusSurface
}

declare global {
  interface Window {
    /**
     * Cached Janus resolution for the current SPA origin. Populated by
     * `getSurface()` on first successful call (or fallback). Read by the
     * sync primitives (`getHostKind`, `isAdminHost`) to avoid duplicate
     * network requests across React StrictMode / route changes.
     */
    __janusSurface?: SurfaceResolution
  }
}
